import {ApiState, getClientType, getStoreName} from '../../common/types/common';
import {
  GroupedAssignedItems,
  ReceiptBypassMode,
  RunForBatchDetail,
  itemsSortBy,
} from '../types';
import {HomeScreens, ShopperScreens} from '../../home/navigation/types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  batchDetailActionsLocal,
  useBatchDetailReducer,
} from '../hooks/batchDetail';
import {useIsFocused, useRoute} from '@react-navigation/native';

import AddBottomSheet from '../addBottomSheet';
import {Analytics} from '../../config/analytics/analytics';
import AssignmentBottmSheet from '../component/AssignmentBottomSheet/AssignmentBottmSheet';
import {BackHeaderLeft} from '../../common/components/BackHeaderLeft';
import {BatchDetailContext} from '../context/BatchDetailContext';
import BatchDetailHeader from '../component/BatchDetailHeader';
import BatchScreenComponent from '../components/BatchScreenComponent/BatchScreenComponent';
import BottomSheet from '../../components/composites/bottomSheet';
import ChatBottomsheet from '@buncha/itemDetails/components/ItemDetail/contactBottomSheet/ChatBottomsheet';
import {CustomItemMode} from '@buncha/itemDetails/types/CustomItem';
import CustomModal from '../../common/components/CustomModal/CustomModal';
import {EventType} from '../../config/analytics/type';
import Header from '../../components/composites/Header/Header';
import HeaderRightSection from '../component/HeaderRightSection';
import If from '../../components/conditional/If';
import {ItemDetailsScreen} from '@buncha/itemDetails/navigation/types';
import {MainScreens} from '../../navigation/types';
import {Modules} from '@buncha/utils/types';
import PackItemList from '../component/PackItemList';
import PassOrderModal from '../component/PassOrderModal/PassOrderModal';
import {SpinnerComponent} from '../../components/core/loader';
import {StyleSheet} from 'react-native';
import listener from '../../config/listener';
import {navigation} from '../../config/navigation';
import {useAppSelector} from '../../config/redux/hooks';
import {useColorThemeType} from '../../appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useGetRunForBatchDetail} from '../hooks/BatchDetails';
import {useIsMeijerStore} from '@buncha/home/hooks/home';

function OrderPackingPage() {
  const theme = useColorThemeType();
  const [state, dispatch] = useBatchDetailReducer();
  const provider = useMemo(() => ({state, dispatch}), [dispatch, state]);
  const {title, body, button} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};

  const [open, setOpen] = useState(false);
  const [hasNavigateToBack, setHasNavigateToBack] = useState(false);
  const [getRunForBatchDetail, apiRunState] = useGetRunForBatchDetail();
  const [runState, setRunState] = useState<ApiState<RunForBatchDetail>>({
    loading: false,
    error: undefined,
    data: undefined,
  });
  const [openBottomSheet, setShowBottomSheet] = useState<boolean>(false);
  const [showAssignmentModal, setShowAssignementModal] =
    useState<boolean>(false);
  const globalState = useAppSelector(gstate => gstate.home);
  const shiftsId = globalState.batchShiftId;
  const shiftKey = globalState.batchShiftKey;
  const storeId = runState?.data?.store?.id ?? 0;
  const isMeijerStore = useIsMeijerStore(storeId);

  const isFocused = useIsFocused();

  const route = useRoute();

  const routeParams: any = route?.params;
  const [runId, setRunId] = useState(routeParams?.runId);

  const contactBottomSheetProps = state.contactSheetProps;
  const tutorial = state.tutorial;

  const navigateToReceiptScreen = useCallback(() => {
    setOpen(true);
    Analytics.eventWithProps('Show shopper receipt modal', {
      type: EventType.cta,
      client: getClientType(isMeijerStore),
      storeType: getStoreName(String(storeId)),
    });
  }, [isMeijerStore, storeId]);

  useEffect(() => {
    if (runState?.data) setRunId(runState?.data?.id);
  }, [runState?.data]);

  const assignmentModal = useCallback((value: boolean) => {
    setShowAssignementModal(value);
  }, []);

  const closeModal = useCallback(() => {
    if (dispatch)
      dispatch(
        batchDetailActionsLocal.setSkippedReceiptFlow(
          ReceiptBypassMode.skipByShopper,
        ),
      );
    if (runId) getRunForBatchDetail(runId);
    setOpen(false);
  }, [dispatch, getRunForBatchDetail, runId]);

  const goBack = useCallback(() => {
    if (tutorial)
      return navigation.navigate(MainScreens.Home, {
        screen: HomeScreens.Tutorials,
      });
    navigation.navigate(MainScreens.Home, {
      screen: HomeScreens.AboutShift,
      params: {
        shiftId: shiftsId,
        shiftKey: shiftKey,
      },
    });
  }, [shiftKey, shiftsId, tutorial]);

  const skipReceiptFlowHandler = useCallback(() => {
    if (dispatch) {
      dispatch(
        batchDetailActionsLocal.setSkippedReceiptFlow(
          ReceiptBypassMode.skipByShopper,
        ),
      );
      closeModal();
    }
  }, [closeModal, dispatch]);

  const renderComponent = useMemo(() => {
    if (open)
      return (
        <BatchScreenComponent
          runState={runState?.data}
          isOpen={open}
          onClose={closeModal}
          runId={runId}
          hasSkip={state?.hasSkipReceipt}
          hasSkipReceiptHandler={skipReceiptFlowHandler}
        />
      );
  }, [
    closeModal,
    open,
    runId,
    runState?.data,
    skipReceiptFlowHandler,
    state?.hasSkipReceipt,
  ]);

  const closeContactSheet = useCallback(() => {
    if (dispatch)
      dispatch(batchDetailActionsLocal.setContactBottomSheetProps(undefined));
  }, [dispatch]);

  const refreshRun = useCallback(() => {
    if (runId) getRunForBatchDetail(runId);
  }, [getRunForBatchDetail, runId]);

  const closeAssignmentModal = useCallback(() => {
    setShowAssignementModal(false);
    setShowBottomSheet(true);
  }, []);

  const navigateToCheckTask = useCallback(() => {
    dispatch(batchDetailActionsLocal.setPickedOrderFoundModal(false));
    setHasNavigateToBack(true);
  }, [dispatch]);

  useEffect(() => {
    if (hasNavigateToBack && !state.hasPickedOrderModal) {
      navigation.replace(HomeScreens.Shopper, {
        screen: ShopperScreens.BatchDetailsPage,
        params: {runId: runId},
      });
      setHasNavigateToBack(false);
    }
  }, [hasNavigateToBack, runId, state.hasPickedOrderModal]);

  const closeBottomSheet = useCallback(() => {
    setShowBottomSheet(false);
  }, []);

  useEffect(() => {
    if (routeParams.tutorial === 'true' && dispatch)
      dispatch(batchDetailActionsLocal.setTutorial(true));
    const itemStatusCount = listener.addListener(
      'orderPackRefresh',
      refreshRun,
    );
    return itemStatusCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (runId) getRunForBatchDetail(runId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, isFocused]);

  useEffect(() => {
    if (apiRunState.data) setRunState(apiRunState);
  }, [apiRunState]);

  const navigateToAddItem = useCallback(
    (item?: GroupedAssignedItems) => {
      closeContactSheet();
      if (isMeijerStore && !state.addItem)
        return dispatch(batchDetailActionsLocal.setAddItem(item));
      const orderId = item?.orderItems?.length
        ? item?.orderItems[0]?.orderId
        : 0;
      const storeIndex = runState?.data?.store?.elasticSearchIndex || '';
      if (!runId || !storeId || !orderId) return;
      if (!runState?.data?.store?.elasticSearchIndex?.length) {
        Analytics.eventWithProps('Navigate to Add Custom Item', {
          type: EventType.cta,
          runID: runId,
          client: getClientType(isMeijerStore),
          storeType: getStoreName(String(storeId)),
          orderId,
          storeId,
        });

        return navigation.navigate(ShopperScreens.ItemDetails, {
          screen: ItemDetailsScreen.CustomItem,
          params: {
            orderId,
            runId,
            mode: CustomItemMode.addItem,
            returnPage: ShopperScreens.OrderPackingPage,
          },
        });
      }
      Analytics.eventWithProps('Navigate to Add Item', {
        type: EventType.cta,
        runID: runId,
        client: getClientType(isMeijerStore),
        storeType: getStoreName(String(storeId)),
        orderId,
        storeId,
      });
      navigation.navigate(ShopperScreens.AddItemPage, {
        storeId,
        runId,
        orderId,
        storeIndex,
        returnPage: ShopperScreens.OrderPackingPage,
      });
    },
    [
      closeContactSheet,
      isMeijerStore,
      state.addItem,
      dispatch,
      runState?.data?.store?.elasticSearchIndex,
      runId,
      storeId,
    ],
  );

  const clearAddItem = useCallback(
    () => dispatch(batchDetailActionsLocal.setAddItem(undefined)),
    [dispatch],
  );

  useEffect(() => {
    if (runId && dispatch) {
      Analytics.eventWithProps('Batch Details Screen', {
        type: EventType.page,
        runID: runId,
        storeName: runState?.data?.storeName,
        status: 'In Packing Status',
        client: getClientType(isMeijerStore),
        storeType: getStoreName(String(storeId)),
      });
      dispatch(batchDetailActionsLocal.setSortBarValue(itemsSortBy.customer));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  return (
    <Header
      style={styles.parentBox}
      headerLeftComponent={<BackHeaderLeft onClick={goBack} />}
      headerRightComponent={
        <HeaderRightSection
          navigateToReceiptScreen={navigateToReceiptScreen}
          runId={runState.data?.id || 0}
          isMeijerStore={isMeijerStore}
        />
      }
      headerColor={theme?.background.blue[300]}>
      <BatchDetailContext.Provider value={provider}>
        <If condition={runState.loading && !showAssignmentModal}>
          <SpinnerComponent style={styles.loaderStyle} />
        </If>
        <If condition={!runState.loading || showAssignmentModal}>
          <If condition={runState.data}>
            <BatchDetailHeader
              showRunPill={true}
              runDetail={runState.data ?? undefined}
            />
          </If>
          <PackItemList
            runDetail={runState.data ?? undefined}
            navigateToReceiptScreen={navigateToReceiptScreen}
            assignmentModal={assignmentModal}
            navigateToAddItem={navigateToAddItem}
          />
          <BottomSheet
            bgColor={theme?.background.white[100]}
            open={openBottomSheet}
            onClose={() => {
              closeBottomSheet();
            }}>
            <AssignmentBottmSheet
              tutorial={tutorial}
              runId={runId}
              closeSheet={() => closeBottomSheet()}
              dispatch={dispatch}
              orderIds={state.orderIdsToBeSend}
              isB2B={isMeijerStore}
            />
          </BottomSheet>
        </If>
        {renderComponent}
        <If condition={state?.hasPickedOrderModal}>
          <CustomModal
            open={state?.hasPickedOrderModal}
            onClose={() => {
              dispatch(batchDetailActionsLocal.setPickedOrderFoundModal(false));
            }}>
            <PassOrderModal
              onContinueClick={() => navigateToCheckTask()}
              title={title?.actionRequired}
              ctaTitle={button?.checkTask}
              subtitle={body?.checkOrders}
            />
          </CustomModal>
        </If>
        <If condition={state?.orderAssignee && showAssignmentModal}>
          <CustomModal
            open={showAssignmentModal}
            onClose={() => closeAssignmentModal()}>
            <PassOrderModal
              onContinueClick={() => closeAssignmentModal()}
              title={title?.orderAssigned}
              ctaTitle={button?.continue}
              subtitle={state.orderAssignee}
            />
          </CustomModal>
        </If>

        <If condition={contactBottomSheetProps}>
          <ChatBottomsheet
            isOpen={Boolean(contactBottomSheetProps)}
            onClose={closeContactSheet}
            orderId={contactBottomSheetProps?.orderId}
            orderItemId={contactBottomSheetProps?.orderItemId}
            runId={runId}
            storeId={storeId}
          />
        </If>

        <If condition={state.addItem}>
          <AddBottomSheet
            storeIndex={runState.data?.store?.elasticSearchIndex}
            open={Boolean(state.addItem)}
            onClose={clearAddItem}
            navigateToAddScreen={navigateToAddItem}
            item={state.addItem}
            orderId={state.addItem?.metaData?.orderId}
            runId={runId}
            storeId={runState.data?.store?.id}
          />
        </If>
      </BatchDetailContext.Provider>
    </Header>
  );
}

export default React.memo(OrderPackingPage);

const styles = StyleSheet.create({
  loaderStyle: {
    marginTop: '50%',
  },
  parentBox: {
    flex: 1,
  },
});
